import React, { useState } from 'react';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import StatusCheckbox from '../StatusCheckbox/StatusCheckbox';
import { ORDER_STATUSES, CHECKBOX_STATUSES } from '../../../constants/orderStatuses';
import './OrderDetailModal.css';

const OrderDetailModal = ({ 
  order, 
  isOpen, 
  onClose, 
  onStatusChange, 
  onSaveChanges 
}) => {
  const [loading, setLoading] = useState(false);
  const [itemChanges, setItemChanges] = useState({});
  const [isClosing, setIsClosing] = useState(false);

  if (!isOpen || !order) return null;

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300); // Match the CSS animation duration
  };

  const handleItemChange = (itemId, field, value) => {
    setItemChanges(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  const handleSaveChanges = async () => {
    if (Object.keys(itemChanges).length === 0) {
      handleClose();
      return;
    }

    setLoading(true);
    try {
      const changes = Object.entries(itemChanges).map(([itemId, changes]) => ({
        idPedProduto: parseInt(itemId),
        ...changes
      }));

      await onSaveChanges({
        pedidoId: order.idPed,
        tenantId: order.tenantId,
        changes
      });

      handleClose();
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Erro ao salvar as alterações. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getItemChange = (itemId, field) => {
    return itemChanges[itemId]?.[field] || '';
  };

  const hasItemChanges = (itemId) => {
    return itemChanges[itemId] && Object.keys(itemChanges[itemId]).length > 0;
  };

  return (
    <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Detalhes do Pedido #{order.idPedido}</h2>
            <div className="order-date">Empresa {order.tenantId}</div>
          </div>
          <button className="modal-close" onClick={handleClose}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Order Information */}
          <div className="order-info-section">
            <div className="info-grid">
              <div className="info-item">
                <label>Cliente:</label>
                <span>{order.fantasiaCliente}</span>
              </div>
              <div className="info-item">
                <label>Data do Pedido:</label>
                <span>{new Date(order.dataPedido).toLocaleDateString('pt-BR')}</span>
              </div>
              <div className="info-item">
                <label>Empresa:</label>
                <span>{order.tenantId}</span>
              </div>
              <div className="info-item">
                <label>Status da Operação:</label>
                <select 
                  className={`status-select ${order.statusOperacao.toLowerCase().replace(' ', '-')}`}
                  value={order.statusOperacao}
                  onChange={(e) => {
                    // TODO: Implement status change
                    console.log('Status changed to:', e.target.value);
                  }}
                >
                  {Object.values(ORDER_STATUSES).map(status => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="info-item">
                <label>Referência:</label>
                <span>{order.referenciaPedido || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Localização:</label>
                <span>{order.bairroCliente}, {order.cidadeCliente} - {order.ufCliente}</span>
              </div>
              <div className="info-item">
                <label>Separação:</label>
                <StatusCheckbox
                  orderId={order.idPed}
                  tenantId={order.tenantId}
                  statusType="separacao"
                  isChecked={order.statusSeparacao === CHECKBOX_STATUSES.CONCLUIDO}
                  onStatusChange={onStatusChange}
                />
              </div>
              <div className="info-item">
                <label>Produção:</label>
                <StatusCheckbox
                  orderId={order.idPed}
                  tenantId={order.tenantId}
                  statusType="producao"
                  isChecked={order.statusProducao === CHECKBOX_STATUSES.CONCLUIDO}
                  onStatusChange={onStatusChange}
                />
              </div>
            </div>
            
            {order.obsPedido && (
              <div className="info-item full-width">
                <label>Observação do Pedido:</label>
                <span>{order.obsPedido}</span>
              </div>
            )}
            
            {order.observacaoCliente && (
              <div className="info-item full-width">
                <label>Observação do Cliente:</label>
                <span>{order.observacaoCliente}</span>
              </div>
            )}
          </div>

          {/* Items Section */}
          <div className="items-section">
            <h3>Itens do Pedido ({order.itens.length})</h3>
            <div className="items-table-container">
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Descrição</th>
                    <th>Qtd. Original</th>
                    <th>Qtd. Atualizada</th>
                    <th>Observação</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {order.itens.map((item) => {
                    const atualizacao = order.atualizacoesDeItens?.find(a => a.idPedProduto === item.idPedProduto);
                    const qtdOriginal = parseFloat(item.qtdeProduto) || 0;
                    const qtdAtualizada = atualizacao?.quantidade || qtdOriginal;
                    const hasChanges = hasItemChanges(item.idPedProduto);
                    
                    return (
                      <tr 
                        key={item.idPedProduto} 
                        className={`item-row ${hasChanges ? 'has-changes' : ''}`}
                        data-item-id={item.idPedProduto}
                      >
                        <td className="item-code">{item.idProduto}</td>
                        <td className="item-description">{item.descProduto}</td>
                        <td className="item-original-qty">{Math.round(qtdOriginal)}</td>
                        <td className="item-updated-qty">
                          <input
                            type="number"
                            className="qty-input"
                            value={getItemChange(item.idPedProduto, 'quantidade') || Math.round(qtdAtualizada)}
                            min="0"
                            step="1"
                            data-original={Math.round(qtdOriginal)}
                            onChange={(e) => handleItemChange(item.idPedProduto, 'quantidade', parseInt(e.target.value) || 0)}
                          />
                        </td>
                        <td className="item-observation">
                          <input
                            type="text"
                            className="obs-input"
                            value={getItemChange(item.idPedProduto, 'observacao') || atualizacao?.observacao || ''}
                            placeholder="Observação..."
                            onChange={(e) => handleItemChange(item.idPedProduto, 'observacao', e.target.value)}
                          />
                        </td>
                        <td className="item-status">
                          {hasChanges ? (
                            <span className="status-modified">Modificado</span>
                          ) : (
                            <span className="status-original">Original</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={handleClose} disabled={loading}>
              Cancelar
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSaveChanges} 
              disabled={loading}
            >
              {loading ? (
                <LoadingSpinner size="small" text="Salvando..." />
              ) : (
                'Salvar Alterações'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
